import { Component, EventEmitter, ViewChild } from '@angular/core';
import {
  jwtDecode,
  PanAppState,
  PlatformService,
  Tracking,
} from '@panamax/app-state';
import { TranslateService } from '@ngx-translate/core';
import {
  IonContent,
  ViewDidEnter,
  ViewWillEnter,
  ViewWillLeave,
} from '@ionic/angular';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { PATHS } from '@shared/constants/paths';
import { environment } from 'src/environments/environment';
import {
  BrowseHierarchyFacadeService,
  PERSONALIZATION_TYPE,
  ProductAnalyticsService,
} from '@usf/ngrx-product';
import { BrowseHierarchyViewModel } from '@usf/ngrx-product/lib/models/browse-hierarchy-state.model';
import { combineLatest, Observable, of, Subscription, tap } from 'rxjs';
import { MerchandisingProductViewModel } from '@shared/models/merchandising-product-view.model';
import { UserService } from '@app/user/services';
import { QuantityUpdate } from '@shared/models/quantity-update';
import { OrderItemService } from '@shared/services/order-item.service';
import { RECENTLY_VIEWED, TOP_SELLERS } from '@shared/constants/home-constants';
import { filter, switchMap, take } from 'rxjs/operators';
import { MerchandisingService } from '@shared/services/merchandising/merchandising.service';
import {
  GUEST_LANDING_PAGE_VARIANT_A,
  GUEST_LANDING_PAGE_VARIANT_B,
} from '@shared/constants/merchandising-constants';
import { TopSectionSearchText } from '@search/shared/models/search-page.model';
import { CustomerSwitchStatus } from '@app/ngrx-customer/constants/customer-switch-status';
import { CustomerStoreService } from '@app/ngrx-customer/services';
import { ProductService } from '@shared/services/product/product.service';
import { ToggleNewSearchService } from '@shared/services/toggle-new-search/toggle-new-search.service';
import { FACET_VALUE } from '@app/new-search/constants/new-search-constants';
import { NewSearchService } from '@app/new-search/services/new-search.service';
import { CoveoService } from '@shared/services/coveo/coveo.service';
import {
  buildRecommendations,
  InstantProducts,
  InteractiveProduct,
  Recommendations,
  Unsubscribe,
} from '@coveo/headless/commerce';
import { FEATURES } from '@shared/constants/splitio-features';
import { ProductDetailViewModel } from '@product-detail/models/product-detail-view-model';
import { PdpLnkSrc } from '@shared/constants/pdp-lnksrc.enum';
import { GuestPageService } from '@shared/services/guest-page/guest-page.service';
import { PersonalizationService } from '@shared/services/personalization/personalization.service';

@Component({
  selector: 'app-browse',
  templateUrl: './browse.page.html',
  styleUrls: ['./browse.page.scss'],
})
export class BrowsePage implements ViewWillEnter, ViewWillLeave, ViewDidEnter {
  @ViewChild(IonContent) content: IonContent;
  browseHierarchyViewModel: BrowseHierarchyViewModel;
  browseHierarchyViewModel$: Subscription;
  isGuestUser$: Subscription;
  sessionSubscription$: Subscription;
  recentlyViewedProducts$: Observable<MerchandisingProductViewModel[]>;
  recentlyViewedIsTracked = false;
  searchPath = '/' + PATHS.SEARCH;
  scoopId = TopSectionSearchText.ALLSCOOP;
  directId = environment.direct;
  neweandsParams = { searchText: 'eandscoreessentials' };

  scoopImageUrl = 'assets/images/browse-deals-scoop-desktop.webp';
  directImageUrl = 'assets/images/catalog-banner-desktop.webp';
  isGuestUser = undefined;
  TOP_SELLER_FEATURE_NAME = TOP_SELLERS;
  RECENTLY_VIEWED_FEATURE_NAME = RECENTLY_VIEWED;
  recentlyViewedProducts: MerchandisingProductViewModel[];
  isDirectEligible = false;
  zipCode: string;
  zipCodeSub$: Subscription;
  zipCodeSwitchSub$: Subscription;
  useRecentlyViewedNewModel = false;

  isNewSearchEnabled = false;
  newSearchSubscription$: Subscription;
  searchBrowseScoopParams = { searchText: this.scoopId };
  searchDirectQueryParams = { searchFilterProperties: this.directId };
  newSearchBrowseScoopParams = {
    facetFilters: `our_exclusives:${FACET_VALUE.ALL_SCOOP}`,
  };
  newSearchDirectQueryParms = {
    facetFilters: `our_exclusives:${FACET_VALUE.DIRECT}`,
  };
  browseScoopParams: any = this.searchBrowseScoopParams;
  directQueryParams: any = this.searchDirectQueryParams;

  splitDynamicGuestAbTestEnabled = undefined;

  commerceEngineSubscription$: Subscription;
  recommendationsController: Recommendations;
  recommendationsControllerUnsubscribe: Unsubscribe;
  newTopSellersProductNumbers: number[] = [];
  newTopSellers$: Subscription;
  newTopSellers: ProductDetailViewModel[];
  featureFlagSubscription$: Subscription;
  productCardClickEvent = new EventEmitter<ProductDetailViewModel>();
  lnkSrc = PdpLnkSrc.topSellersNullSearchResults;

  instantProductsController: InstantProducts;

  constructor(
    readonly platformService: PlatformService,
    private translateService: TranslateService,
    private orderItemService: OrderItemService,
    protected panAppState: PanAppState,
    private browseHierarchyFacadeService: BrowseHierarchyFacadeService,
    private userService: UserService,
    private productAnalyticService: ProductAnalyticsService,
    private merchandisingService: MerchandisingService,
    private customerStoreService: CustomerStoreService,
    private productService: ProductService,
    private readonly toggleNewSearchService: ToggleNewSearchService,
    private newSearchService: NewSearchService,
    private coveoService: CoveoService,
    private guestPageService: GuestPageService,
    private personalizationService: PersonalizationService,
  ) {
    this.featureFlagSubscription$ = combineLatest([
      this.panAppState.feature$([
        FEATURES.split_dynamic_recently_viewed_new_model.name,
      ]),
    ]).subscribe(([useRecentlyViewedNewModel]) => {
      this.useRecentlyViewedNewModel = useRecentlyViewedNewModel;
    });
  }

  ionViewWillEnter(): void {
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.browseProducts),
    );
    this.isGuestUser$ = combineLatest([
      this.userService.isGuestUser$(),
      this.panAppState.feature$([
        FEATURES.split_dynamic_recently_viewed_new_model.name,
      ]),
    ]).subscribe(([isGuest, useRecentlyViewedNewModel]) => {
      this.isGuestUser = isGuest;
      useRecentlyViewedNewModel && this.fetchAndLoadNewRecentlyViewed();
    });
    this.verifySplitDynamicGuestAbTestValue();
    this.browseHierarchyFacadeService.loadBrowseCategories();
    this.browseHierarchyViewModel$ = this.browseHierarchyFacadeService
      .getBrowseHierarchyViewModel()
      .subscribe(value => {
        this.browseHierarchyViewModel = value;
        this.browseHierarchyViewModel.categories.sort((a, b) =>
          a.classDescription.localeCompare(b.classDescription),
        );
      });

    const platformType = this.platformService.getPlatformType();
    switch (platformType) {
      case 'desktop':
        this.scoopImageUrl = 'assets/images/browse-deals-scoop-desktop.webp';
        this.directImageUrl = 'assets/images/catalog-banner-desktop.webp';
        break;
      case 'tablet':
        this.scoopImageUrl = 'assets/images/browse-deals-scoop-tablet.webp';
        this.directImageUrl = 'assets/images/catalog-banner-tablet.webp';
        break;
      case 'mobile':
        this.scoopImageUrl = 'assets/images/browse-deals-scoop-mobile.webp';
        this.directImageUrl = 'assets/images/catalog-banner-mobile.webp';
        break;
    }

    this.sessionSubscription$ = this.customerStoreService
      .customerSwitchStatus$()
      .pipe(filter(status => status === CustomerSwitchStatus.complete))
      .subscribe(() => {
        this.checkIsDirect();
      });

    this.newSearchSubscription$ =
      this.toggleNewSearchService.newSearchEnabledObservable$.subscribe(
        enabled => {
          this.isNewSearchEnabled = enabled;
          if (enabled) {
            this.searchPath = '/' + PATHS.NEW_SEARCH;
            this.browseScoopParams = this.newSearchBrowseScoopParams;
            this.directQueryParams = this.newSearchDirectQueryParms;
          } else {
            this.searchPath = '/' + PATHS.SEARCH;
            this.browseScoopParams = this.searchBrowseScoopParams;
            this.directQueryParams = this.searchDirectQueryParams;
          }
        },
      );
  }

  async verifySplitDynamicGuestAbTestValue() {
    this.splitDynamicGuestAbTestEnabled =
      this.guestPageService.getSplitDynamicGuestAbTestEnabledSnapshot();

    const variantValue = this.splitDynamicGuestAbTestEnabled
      ? GUEST_LANDING_PAGE_VARIANT_B
      : GUEST_LANDING_PAGE_VARIANT_A;

    this.productAnalyticService.trackSearchCatalogBrowsePageLoad(variantValue);

    this.initializeCoveo();
  }

  initializeCoveo() {
    this.commerceEngineSubscription$ = this.coveoService
      .getCommerceEngine$()
      .pipe(filter(engine => !!engine))
      .subscribe(engine => {
        this.recommendationsController = buildRecommendations(engine, {
          options: {
            slotId: `${environment.coveoRecommendationsSlots.topSellersBrowse}`,
          },
        });
        this.recommendationsControllerUnsubscribe =
          this.recommendationsController.subscribe(() => {
            this.loadNewTopSellers();
          });
        this.recommendationsController.refresh();
      });
  }

  private trackCarouselLoaded(
    products: ProductDetailViewModel[],
    featureName: string,
    event: string,
  ) {
    const productsTracking = products.map(p => ({
      attributes: p.trackingAttributes,
      divisionApn: `${p?.summary?.divisionNumber}-${p?.productNumber}`,
      merchFeatureView: '1',
      merchFeatureName: featureName,
    }));
    this.productAnalyticService.trackMerchCarouselLoaded(
      productsTracking,
      event,
    );
  }

  loadNewTopSellers() {
    this.newTopSellersProductNumbers = this.recommendationsController?.state[
      'products'
    ]?.map(product => Number(product.ec_product_id));

    this.newTopSellers$ = this.newSearchService
      .getTopSellersViewModels$(this.newTopSellersProductNumbers)
      .subscribe(products => {
        this.newTopSellers = products.slice(0, 12);
        this.trackCarouselLoaded(
          this.newTopSellers,
          this.TOP_SELLER_FEATURE_NAME,
          'top seller browse page displayed',
        );
      });
  }
  fetchAndLoadNewRecentlyViewed() {
    if (!this.isGuestUser) {
      this.personalizationService
        .fetchPersonalization(PERSONALIZATION_TYPE.recentlyViewed)
        .pipe(
          switchMap(isEnabled =>
            isEnabled
              ? this.personalizationService.getPersonalizationProductNumbers$(
                  PERSONALIZATION_TYPE.recentlyViewed,
                )
              : of(null),
          ),
          filter(productNumbers => !!productNumbers),
          take(1),
        )
        .subscribe(productNumbers => {
          this.productService.loadProducts(productNumbers);
        });
      this.recentlyViewedProducts$ = this.personalizationService
        .loadPersonalizationProducts$(PERSONALIZATION_TYPE.recentlyViewed)
        .pipe(
          tap(products => {
            if (!this.recentlyViewedIsTracked && !!products) {
              const productsTracking = [];
              products.forEach(p => {
                productsTracking.push({
                  attributes: p.trackingAttributes,
                  divisionApn: `${p?.summary?.divisionNumber}-${p?.productNumber}`,
                  merchFeatureView: '1',
                  merchFeatureName: this.RECENTLY_VIEWED_FEATURE_NAME,
                });
              });
              this.productAnalyticService.trackMerchCarouselLoaded(
                productsTracking,
                'recently viewed displayed',
              );
              this.recentlyViewedIsTracked = true;
            }
          }),
        );
    }
  }

  onTopSellerQuantityUpdate(data: {
    quantityUpdate: QuantityUpdate;
    index: number;
  }) {
    const trackingData: Tracking = {
      analytics: {
        data: {
          products: [
            {
              addMethod: this.TOP_SELLER_FEATURE_NAME,
              merchFeatureName: this.TOP_SELLER_FEATURE_NAME,
            },
          ],
          results: {
            resultRank: data.index + 1,
          },
        },
      },
    };

    this.orderItemService.changeQuantity(
      data.quantityUpdate.productNumber,
      data.quantityUpdate.quantity,
      data.quantityUpdate.isEaches,
      trackingData,
    );
  }

  onRecentlyViewedQuantityUpdate(data: {
    quantityUpdate: QuantityUpdate;
    index: number;
  }) {
    const trackingData: Tracking = {
      analytics: {
        data: {
          products: [
            {
              addMethod: this.RECENTLY_VIEWED_FEATURE_NAME,
              merchFeatureName: this.RECENTLY_VIEWED_FEATURE_NAME,
            },
          ],
          results: {
            resultRank: data.index + 1,
          },
        },
      },
    };

    this.orderItemService.changeQuantity(
      data.quantityUpdate.productNumber,
      data.quantityUpdate.quantity,
      data.quantityUpdate.isEaches,
      trackingData,
    );
  }

  checkIsDirect(): void {
    this.panAppState.accessToken$
      .pipe(
        filter(token => !!token),
        take(1),
      )
      .subscribe(token => {
        const decoded = jwtDecode(token);
        if (decoded) {
          this.isDirectEligible = decoded['usf-claims'].directEligible;
        }
      });
  }

  emitProductCardClick(product: ProductDetailViewModel) {
    const coveoProduct = this.recommendationsController.state.products.find(
      p => p.ec_product_id == product.productNumber.toString(),
    );

    if (coveoProduct) {
      coveoProduct.ec_price =
        product?.pricing?.unitPrice ?? product?.pricing?.eachPrice;
    }
    const interactiveProduct: InteractiveProduct =
      this.instantProductsController.interactiveProduct({
        options: {
          product: coveoProduct,
        },
      });
    interactiveProduct.select();
  }

  ionViewDidEnter(): void {
    this.content.scrollToTop();
  }

  ionViewWillLeave(): void {
    this.browseHierarchyViewModel$.unsubscribe();
    this.isGuestUser$.unsubscribe();
    this.sessionSubscription$?.unsubscribe();
    this.newSearchSubscription$?.unsubscribe();
    this.commerceEngineSubscription$?.unsubscribe();
    this.featureFlagSubscription$?.unsubscribe();
    if (this.recommendationsControllerUnsubscribe) {
      this.recommendationsControllerUnsubscribe();
    }
    this.newTopSellers$?.unsubscribe();
  }
}
